import { useEffect, useState } from "react";
import { Badge, Box, Button, HStack, Input, Select, Text } from "@chakra-ui/react";
import { CustomToast } from "../../components";
import { useFetch } from "../../hooks/useFetch";
import { showDate } from "../../utilities/helper";
import { cardProps } from "./billingui";
import CustomExpandTable from "../../components/CustomExpandTable";
import { Column } from "../../components/CustomExpandTable/customExpandTable";
import { formatMoney } from "../../utilities/money";

interface Props {
  projectId: number;
  editingId?: number | null;
  reloadKey?: number;
  onEdit: (billingId: number) => void;
}
const PAGE_SIZE = 10;

export default function BillingPlanList({ projectId, editingId, reloadKey = 0, onEdit }: Props) {
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);

  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
  setLoading(true);
  const params = new URLSearchParams();
  params.append("projectId", String(projectId));
  if (active) params.append("active", active);
  params.append("page", String(page));
  params.append("pageSize", String(PAGE_SIZE));

  fetchApi<any>(`billing-plans?${params.toString()}`, "GET", null, null, "")
    .then((res) => {
      if (res) {
        setRows(res.items);
        setTotal(res.total);
      }
    })
    .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [projectId, page, active, reloadKey]);

  const from = rows.length ? (page - 1) * PAGE_SIZE + 1 : 0;
  const to = rows.length ? from + rows.length - 1 : 0;

  return (
    <Box {...cardProps} p={5}>
      <Text fontWeight="bold" mb={3}>
        Billing Plans
      </Text>

      <HStack mb={4} spacing={3}>
        <Input
          size="sm"
          borderRadius="md"
          maxW="260px"
          placeholder="Search project..."
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />
        <Select
          size="sm"
          borderRadius="md"
          maxW="160px"
          placeholder="All plans"
          value={active}
          onChange={(e) => {
            setPage(1);
            setActive(e.target.value);
          }}
        >
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </Select>
      </HStack>

      <CustomExpandTable value={rows} loading={loading} rowKey="id" headerBg="#FFFFFF" headerTextColor="#52627A" emptyMessage="No billing plans yet" striped={false} renderExpandedContent={() => null}>
        <Column
          header="Project"
          body={(r: any) => (
            <HStack>
              <Text fontSize="13px" fontWeight="600">
                {r.project}
              </Text>
           {editingId === r.id && <Badge colorScheme="blue">Editing</Badge>}
            </HStack>
          )}
        />
        <Column header="Type" body={(r: any) => <Text fontSize="13px">{r.billingType === "OneTime" ? "One-time" : `${r.billingType} · ${r.billingFrequency}`}</Text>} />
        <Column header="Currency" body={(r: any) => <Badge>{r.currencyCode || "INR"}</Badge>} />
        <Column
          header="Amount"
          body={(r: any) => (
            <Text fontSize="13px" whiteSpace="nowrap">
              {formatMoney(r.invoiceAmount, r.currencyCode)}
            </Text>
          )}
        />
        <Column header="GST" body={(r: any) => <Text fontSize="13px">{r.gstPercent}%</Text>} />
        <Column
          header="Next invoice"
          body={(r: any) => (
            <Text fontSize="13px" color="gray.600" whiteSpace="nowrap">
              {r.nextInvoiceDate ? showDate(r.nextInvoiceDate) : "-"}
            </Text>
          )}
        />
        <Column header="Auto-generate" body={(r: any) => <Badge colorScheme={r.autoGenerateInvoice ? "green" : "gray"}>{r.autoGenerateInvoice ? "On" : "Off"}</Badge>} />
        <Column header="Status" body={(r: any) => <Badge colorScheme={r.isActive ? "green" : "red"}>{r.isActive ? "Active" : "Inactive"}</Badge>} />
        <Column header="Invoices" body={(r: any) => <Text fontSize="13px">{r.invoiceCount}</Text>} />
        <Column
          header=""
          body={(r: any) => (
            <Button size="xs" variant="outline" colorScheme="blue" onClick={() => onEdit(r.id)}>
              Edit
            </Button>
          )}
        />
      </CustomExpandTable>

      <HStack justify="space-between" mt={4}>
        <Text fontSize="xs" color="gray.500">
          Showing {from}–{to} of {total}
        </Text>
        <HStack spacing={2}>
          <Button size="xs" variant="outline" onClick={() => setPage((p) => p - 1)} isDisabled={page === 1}>
            Prev
          </Button>
          <Button size="xs" colorScheme="blue" cursor="default" pointerEvents="none">
            {page}
          </Button>
          <Button size="xs" variant="outline" onClick={() => setPage((p) => p + 1)} isDisabled={page * PAGE_SIZE >= total}>
            Next
          </Button>
        </HStack>
      </HStack>
    </Box>
  );
}
